/*
 * tq-roasting — the native "have this coffee roasted" option on the PDP
 * (replaces the Avis Plus Product Options app).
 *
 * Markup comes from snippets/tq-roasting-options.liquid (which renders the
 * fieldsets of snippets/tq-roasting-fields.liquid). The element reveals itself
 * on connect (no-JS visitors never see the option) and:
 *
 *  - follows the selected variant through PUB_SUB_EVENTS.variantChange: the
 *    switch is only shown when the variant has an entry in the size→fee map
 *    the snippet serialized (15 LB today);
 *  - walks the question chain (roast level → packaging → bag colour), keeping
 *    hidden groups `disabled` so they can never validate or submit;
 *  - hides the dynamic-checkout button while the switch is ON — accelerated
 *    checkout skips the cart and would silently drop the fee lines;
 *  - intercepts the product form's submit in the CAPTURE phase on `document`
 *    (guaranteed to run before product-form.js's own listener on the form) and
 *    replaces it with ONE atomic JSON `items[]` POST to /cart/add.js: coffee
 *    (with its visible properties + `_tq_roasted` marker) + roasting fee
 *    (+ packaging fee when a bag is chosen), both fee lines carrying
 *    `_tq_for = "<coffee variant id>|<fingerprint>"` so tq-roasting-cart.js
 *    can keep their quantities in lock-step with the coffee line.
 *
 * The stock `name="id"` input is never mixed with `items[]` naming: while the
 * switch is OFF the stock FormData pipeline runs completely untouched.
 */
if (!customElements.get('tq-roasting-options')) {
  customElements.define(
    'tq-roasting-options',
    class TqRoastingOptions extends HTMLElement {
      connectedCallback() {
        try {
          this.map = JSON.parse(this.querySelector('[data-tq-roasting-map]').textContent);
        } catch (e) {
          this.map = {};
        }
        this.form = document.getElementById(this.dataset.formId);
        if (!this.form || !Object.keys(this.map).length) return;

        this.switchInput = this.querySelector('[data-tq-roasting-switch]');
        this.note = this.querySelector('[data-tq-roasting-note]');
        this.fields = this.querySelector('.tq-roasting__fields');
        this.errorEl = this.querySelector('[data-tq-roasting-error]');
        this.pkgSelect = this.querySelector('fieldset[data-fee] select');
        this.colorFs = this.querySelector('fieldset[data-color-fs]');

        this.switchInput.addEventListener('change', () => this.syncChain());
        if (this.pkgSelect) this.pkgSelect.addEventListener('change', () => this.syncColor());

        this.onSubmit = this.onSubmit.bind(this);
        document.addEventListener('submit', this.onSubmit, true);

        // Bare PUB_SUB_EVENTS: the top-level const of constants.js is visible
        // across scripts but never becomes a window property.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (!data || data.sectionId !== this.dataset.sectionId) return;
            this.syncVariant(data.variant ? Number(data.variant.id) : null);
          });
        }

        this.syncVariant(this.currentVariantId());
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmit, true);
        if (this.unsubscribe) this.unsubscribe();
      }

      currentVariantId() {
        const input = this.form.querySelector('[name=id]');
        return input ? Number(input.value) : null;
      }

      entry() {
        return (this.variantId && this.map[String(this.variantId)]) || null;
      }

      // Show/hide the whole option for the (newly) selected variant. Leaving
      // the offer resets the switch so a later return starts clean.
      syncVariant(variantId) {
        this.variantId = variantId || this.currentVariantId();
        const entry = this.entry();
        if (entry) {
          this.note.textContent = entry.note;
        } else {
          this.switchInput.checked = false;
        }
        this.hidden = !entry;
        this.syncChain();
      }

      syncChain() {
        const on = !this.hidden && this.switchInput.checked;
        this.fields.hidden = !on;
        this.fields.querySelectorAll('fieldset').forEach((fs) => {
          if (!fs.hasAttribute('data-color-fs')) fs.disabled = !on;
        });
        if (on) this.syncColor();
        else if (this.colorFs) {
          this.colorFs.hidden = true;
          this.colorFs.disabled = true;
        }
        if (this.errorEl) this.errorEl.hidden = true;
        // Accelerated checkout (Buy it now) would bypass the fee lines.
        const scope = this.closest('product-info') || document.body;
        scope.classList.toggle('tq-roasting-on', on);
      }

      // Bag colour only applies when a real bag is chosen (not bulk), and only
      // in the colours that bag exists in (option[data-colors]); a single
      // allowed colour is checked automatically.
      syncColor() {
        if (!this.colorFs) return;
        const opt = this.pkgSelect && this.pkgSelect.options[this.pkgSelect.selectedIndex];
        const isBag = opt && opt.dataset.variant;
        if (!isBag) {
          this.colorFs.hidden = true;
          this.colorFs.disabled = true;
          return;
        }
        const colors = (opt.dataset.colors || 'kraft').split(',');
        this.colorFs.hidden = false;
        this.colorFs.disabled = false;
        this.colorFs.querySelectorAll('label[data-color]').forEach((label) => {
          const allowed = colors.includes(label.dataset.color);
          const input = label.querySelector('input');
          label.hidden = !allowed;
          input.disabled = !allowed;
          if (!allowed) input.checked = false;
        });
        const enabled = this.colorFs.querySelectorAll('input:not(:disabled)');
        if (enabled.length === 1) enabled[0].checked = true;
      }

      onSubmit(e) {
        if (e.target !== this.form) return;
        if (this.hidden || !this.switchInput.checked) return; // stock pipeline
        const entry = this.entry();
        if (!entry) return;
        e.preventDefault();
        e.stopPropagation();

        // The form is novalidate, so enforce the required questions here.
        const roast = this.fields.querySelector('fieldset:not([data-fee]):not([data-color-fs]) input:checked');
        const colorNeeded = this.colorFs && !this.colorFs.disabled;
        const color = colorNeeded ? this.colorFs.querySelector('input:checked') : null;
        if (!roast || (colorNeeded && !color)) {
          if (this.errorEl) this.errorEl.hidden = false;
          return;
        }
        if (this.errorEl) this.errorEl.hidden = true;

        const qtyInput = this.form.elements ? this.form.elements.quantity : null;
        const qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);

        // Same collection walk as the Café cuit popup (assets/tq-catalog.js):
        // data-prop = property key, the data-fee group may carry a paid variant.
        const props = {};
        let packagingId = null;
        this.querySelectorAll('fieldset[data-prop]').forEach((fs) => {
          if (fs.disabled || fs.hidden) return;
          const sel = fs.querySelector('select');
          const chosen = sel ? sel.options[sel.selectedIndex] : fs.querySelector('input:checked');
          if (!chosen) return;
          if (fs.dataset.fee) {
            if (chosen.dataset.variant) packagingId = Number(chosen.dataset.variant);
            props[fs.dataset.prop] = chosen.dataset.label || chosen.value;
          } else {
            props[fs.dataset.prop] = chosen.value;
          }
        });

        // Deterministic fingerprint: identical configurations merge into the
        // same cart lines; tq-roasting-cart.js keys its reconciliation on it.
        const fp = Object.values(props).join('|');
        props._tq_roasted = fp;
        const link = `${this.variantId}|${fp}`;

        const items = [
          { id: this.variantId, quantity: qty, properties: props },
          { id: entry.roastId, quantity: qty, properties: { _tq_for: link } },
        ];
        if (packagingId) items.push({ id: packagingId, quantity: qty, properties: { _tq_for: link } });

        this.addToCart(items);
      }

      async addToCart(items) {
        const productForm = this.form.closest('product-form');
        const submitButton = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
        const spinner = submitButton && submitButton.querySelector('.loading__spinner');
        if (submitButton) {
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.classList.add('loading');
          if (spinner) spinner.classList.remove('hidden');
        }
        const drawer = document.querySelector('cart-drawer');
        const canDrawer = drawer && typeof drawer.renderContents === 'function';
        const body = { items };
        if (canDrawer) {
          body.sections = drawer.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          drawer.setActiveElement(document.activeElement);
        }
        try {
          const res = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await res.json().catch(() => null);
          if (!res.ok) {
            // Shopify rejects the whole multi-item add if any line fails, so a
            // partial roasted order can never reach the cart.
            const message =
              (data && (data.description || data.message)) ||
              (data && typeof data.errors === 'string' && data.errors) ||
              '';
            if (productForm && typeof productForm.handleErrorMessage === 'function') {
              productForm.handleErrorMessage(message || null);
            }
            return;
          }
          if (canDrawer && data && data.sections) {
            drawer.classList.remove('is-empty');
            drawer.renderContents(data);
          } else {
            window.location = window.routes.cart_url;
          }
        } catch (err) {
          console.error(err);
        } finally {
          if (submitButton) {
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-disabled');
            if (spinner) spinner.classList.add('hidden');
          }
        }
      }
    }
  );
}
